import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { GET_PAGINATED_WORKER_SHIFTS_URL } from "@src/appV2/redesign/WorkerShifts/api/usePaginatedWorkerShifts";
import { useGetWorkplaceProfile } from "@src/appV2/redesign/Workplace/useGetWorkplaceProfile";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery } from "@tanstack/react-query";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

import {
  CancellationType,
  type CancelledShiftDetailsData,
  type CancelledShiftResponse,
  cancelledShiftResponseSchema,
  PayEligibility,
} from "./cancelledShiftTypes";
import { supportQueryKeys } from "./queryKeys";
import { useGetShiftPaymentStatus } from "./useGetShiftPaymentStatus";

interface UseCancelledShiftDetailsParams {
  readonly shiftId: string;
}

interface UseCancelledShiftDetailsResult {
  readonly data: CancelledShiftDetailsData | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/**
 * Fetches a single cancelled shift by ID from the V3 shifts API.
 */
async function fetchCancelledShift(
  workerId: string,
  shiftId: string
): Promise<CancelledShiftResponse> {
  const response = await get({
    url: GET_PAGINATED_WORKER_SHIFTS_URL,
    queryParams: {
      filter: {
        agentId: workerId,
        id: shiftId,
        deleted: true,
      },
      fields: {
        shift: [
          "schedule.startAt",
          "schedule.endAt",
          "schedule.durationWithoutBreaksInHours",
          "requirements.qualificationName",
          "pay.total",
          "deleted",
          "cancellationDetails",
        ],
        workplace: ["name", "timezone"],
      },
      include: ["workplace"],
      sort: {
        field: "schedule.startAt",
        order: "desc",
      },
      page: {
        size: 1,
      },
    },
    responseSchema: cancelledShiftResponseSchema,
  });

  return response.data;
}

function computeLeadTimeMinutes(cancelledAt: string, startAt: string): number {
  return differenceInMinutes(parseISO(startAt), parseISO(cancelledAt));
}

function resolveWorkplaceName(
  shift: CancelledShiftResponse["data"][0],
  included: CancelledShiftResponse["included"]
): string {
  const workplaceId = shift.relationships?.workplace?.data?.id;
  if (!workplaceId || !included) {
    return "Unknown Workplace";
  }

  const workplace = included.find((item) => item.id === workplaceId);
  return workplace?.attributes?.name ?? "Unknown Workplace";
}

function resolveWorkplaceTimezone(
  shift: CancelledShiftResponse["data"][0],
  included: CancelledShiftResponse["included"]
): string {
  const workplaceId = shift.relationships?.workplace?.data?.id;
  if (!workplaceId || !included) {
    return DEFAULT_TIMEZONE;
  }

  const workplace = included.find((item) => item.id === workplaceId);
  return workplace?.attributes?.timezone ?? DEFAULT_TIMEZONE;
}

/**
 * Hook to fetch all data needed for the cancelled shift details screen.
 * Aggregates shift data, workplace policy, payment status, and derived values.
 */
export function useCancelledShiftDetails(
  params: UseCancelledShiftDetailsParams
): UseCancelledShiftDetailsResult {
  const { shiftId } = params;
  const worker = useDefinedWorker();

  const shiftQuery = useQuery({
    queryKey: supportQueryKeys.cancelledShiftDetails(worker.userId, shiftId),
    queryFn: async () => fetchCancelledShift(worker.userId, shiftId),
    enabled: Boolean(shiftId),
    staleTime: 30_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_CANCELLED_SHIFT_DETAILS_FAILURE,
      userErrorMessage: "Failed to load cancelled shift details",
    },
  });

  const shiftData = shiftQuery.data?.data[0];
  const workplaceId = shiftData?.relationships?.workplace?.data?.id;

  const workplaceQuery = useGetWorkplaceProfile(
    {
      workplaceId: workplaceId ?? "",
      projection: {
        lateCancellation: 1,
        sentHomeChargeHours: 1,
        name: 1,
        tmz: 1,
      },
    },
    { enabled: isDefined(workplaceId) }
  );

  const {
    data: paymentStatus,
    isLoading: isPaymentStatusLoading,
    isError: isPaymentStatusError,
    error: paymentStatusError,
    refetch: refetchPaymentStatus,
  } = useGetShiftPaymentStatus({
    shiftId,
    enabled: Boolean(shiftId),
  });

  const cancelledShiftDetails = useMemo((): CancelledShiftDetailsData | undefined => {
    if (!shiftData) {
      return undefined;
    }

    const { attributes } = shiftData;
    const cancellationDetails = attributes?.cancellationDetails;

    if (!cancellationDetails) {
      return undefined;
    }

    const workplaceName = resolveWorkplaceName(shiftData, shiftQuery.data?.included);
    const timezone = resolveWorkplaceTimezone(shiftData, shiftQuery.data?.included);
    const startAt = attributes?.schedule?.startAt ?? "";
    const endAt = attributes?.schedule?.endAt ?? "";

    const leadTimeMinutes = computeLeadTimeMinutes(cancellationDetails.cancelledAt, startAt);

    const cancellationType =
      cancellationDetails.isSentHome === true
        ? CancellationType.SENT_HOME
        : CancellationType.PRE_SHIFT;

    const payEligibility =
      cancellationDetails.isWorkerPaidForCancellation === true
        ? PayEligibility.ELIGIBLE
        : PayEligibility.NOT_ELIGIBLE;

    const workplaceProfile = workplaceQuery.data;

    return {
      shiftId,
      workplaceName,
      workplaceId: workplaceId ?? "",
      startAt,
      endAt,
      timezone,
      qualificationName: attributes?.requirements?.qualificationName,
      cancellationDetails,
      cancellationType,
      leadTimeMinutes,
      payEligibility,
      lateCancellationPeriodHours: workplaceProfile?.lateCancellation?.period ?? 0,
      lateCancellationFeeHours: workplaceProfile?.lateCancellation?.feeHours ?? 0,
      sentHomeChargeHours: workplaceProfile?.sentHomeChargeHours,
      clipboardScoreImpact: "not_affected",
      paymentStatus,
    };
  }, [
    shiftData,
    shiftQuery.data?.included,
    workplaceId,
    workplaceQuery.data,
    paymentStatus,
    shiftId,
  ]);

  const isLoading = shiftQuery.isLoading || workplaceQuery.isLoading || isPaymentStatusLoading;
  const isError = shiftQuery.isError || workplaceQuery.isError || isPaymentStatusError;
  const error =
    [shiftQuery.error, workplaceQuery.error, paymentStatusError].find(
      (queryError): queryError is Error => queryError instanceof Error
    ) ?? null;

  const refetch = (): void => {
    void shiftQuery.refetch();
    void workplaceQuery.refetch();
    refetchPaymentStatus();
  };

  return {
    data: cancelledShiftDetails,
    isLoading,
    isError,
    error,
    refetch,
  };
}
